import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import AppBar from '@mui/material/AppBar'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import Toolbar from '@mui/material/Toolbar'
import Box from '@mui/material/Box'
import MenuIcon from '@mui/icons-material/Menu'
import Button from '@mui/material/Button'
import MuiLink from '@mui/material/Link'
import Typography from '@mui/material/Typography'

import { useContextHook } from '../../store/context'
import { isLoggedIn } from '../../utils/service'
import MachineSelector from './machineSelector'
import CustomerSelector from './customerSelector'
import NavbarItem from './navbarItem'
import Alert from '../alert/alert'
import SystemMessages from '../../utils/systemMessages'

const Navbar = (): JSX.Element => {
  const { t } = useTranslation()
  const { state } = useContextHook()

  const [anchorElNav, setAnchorElNav] = React.useState<HTMLButtonElement | null>(null)

  const leftMenuItems: Array<{ name: string; internalHref: string; badgeCount?: number }> = [
    ...(isLoggedIn()
      ? [
          {
            name: t('navmenu.inventory'),
            internalHref: `/inventories`,
          },
          {
            name: t('navmenu.cupping'),
            internalHref: `/cuppings`,
            badgeCount: state.cuppingRoastIds?.length || 0,
          },
        ]
      : []),
    {
      name: t('navmenu.profilelibrary'),
      internalHref: `/profilelibrary`,
    },
  ]

  const machineMenuItems = state.currentMachine
    ? [
        {
          name: t('navmenu.live'),
          internalHref: `/machine/${state.currentMachine.slug}/live`,
        },
        {
          name: t('navmenu.logs'),
          internalHref: `/machine/${state.currentMachine.slug}/logs`,
        },
        {
          name: t('navmenu.profiles'),
          internalHref: `/machine/${state.currentMachine.slug}/profiles`,
        },
        ...(state.currentMachine.is_p2000
          ? [
              {
                name: t('navmenu.roastschedule'),
                internalHref: `/machine/${state.currentMachine.slug}/schedules`,
              },
            ]
          : []),
      ]
    : []
  const rightMenuItems = state.user
    ? [
        ...(state.user.is_staff
          ? [
              {
                name: t('navmenu.admin'),
                subMenuItems: [
                  {
                    name: t('navmenu.newroaster'),
                    href: `${process.env.REACT_APP_DJANGO_URL}/admin/roest_machines/machine/create_s100/`,
                  },
                  {
                    name: t('navmenu.machines'),
                    href: `${process.env.REACT_APP_DJANGO_URL}/admin/roest_machines/machine/`,
                  },
                  {
                    name: t('navmenu.customers'),
                    href: `${process.env.REACT_APP_DJANGO_URL}/admin/roest_users/customer/`,
                  },
                  { name: t('navmenu.users'), href: `${process.env.REACT_APP_DJANGO_URL}/admin/auth/user/` },
                ],
              },
            ]
          : []),
        {
          name: t('navmenu.support'),
          subMenuItems: [
            {
              name: t('navmenu.sendsupportticket'),
              internalHref: '/support/ticket/',
            },
            {
              name: t('navmenu.supportportal'),
              href: 'https://www.roestcoffee.com/support',
              target: '_blank',
            },
            {
              name: `${process.env.REACT_APP_VERSION_ID} - ${t('navmenu.changelog')}`,
              internalHref: '/support/changelog/',
            },
            {
              name: t('navmenu.firmwarechangelog'),
              internalHref: '/support/firmware-changelog/',
            },
          ],
        },
        {
          name: state.user.username,
          subMenuItems: [
            { name: t('navmenu.settings'), internalHref: '/setup' },
            { name: t('navmenu.logout'), internalHref: '/logout' },
          ],
        },
      ]
    : []

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorElNav(event.currentTarget)
  }

  const handleCloseNavMenu = (): void => {
    setAnchorElNav(null)
  }

  return (
    <AppBar position="static" component="nav" sx={{ backgroundColor: 'navbarHeader.main', mb: 2 }}>
      <Toolbar>
        <Button to={isLoggedIn() ? '/' : '/login'} component={Link} sx={{ p: 0, mr: 6 }}>
          <img src="/logo.png" alt="ROEST" style={{ maxHeight: 25 }} />
        </Button>
        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
          {leftMenuItems.map((menuItem) => (
            <NavbarItem key={menuItem.name} navbarItem={menuItem} />
          ))}
          {state.user?.customers && state.user.customers.length > 1 && <CustomerSelector />}
          {(state.currentMachine || state.machines.length > 0) && <MachineSelector />}
          {machineMenuItems.map((menuItem) => (
            <NavbarItem key={menuItem.name} navbarItem={menuItem} />
          ))}
        </Box>
        <Box sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' } }}>
          {rightMenuItems.map((menuItem) => (
            <NavbarItem key={menuItem.name} navbarItem={menuItem} />
          ))}
        </Box>
        {isLoggedIn() && (
          <Box sx={{ flexGrow: 0, display: { xs: 'flex', md: 'none' } }}>
            <IconButton size="large" onClick={handleOpenNavMenu} color="inherit">
              <MenuIcon />
            </IconButton>
            <Menu id="menu-appbar" anchorEl={anchorElNav} open={Boolean(anchorElNav)} onClose={handleCloseNavMenu}>
              {state.user?.customers && state.user.customers.length > 1 && <CustomerSelector />}
              {leftMenuItems.map((menuItem) => (
                <NavbarItem key={menuItem.name} navbarItem={menuItem} />
              ))}
              {(state.currentMachine || state.machines.length > 0) && <MachineSelector />}
              {machineMenuItems.map((menuItem) => (
                <Box key={menuItem.name} sx={{ pl: 2 }}>
                  <NavbarItem navbarItem={menuItem} />
                </Box>
              ))}
              {rightMenuItems.map((menuItem) => (
                <NavbarItem key={menuItem.name} navbarItem={menuItem} />
              ))}
            </Menu>
          </Box>
        )}
      </Toolbar>
      {state.user && !state.user.email && (
        <Alert severity="warning">
          <Typography>
            {t('navmenu.noemailset')}
            <MuiLink component={Link} to="/setup" sx={{ ml: 1 }}>
              {t('navmenu.setemail')}
            </MuiLink>
          </Typography>
        </Alert>
      )}
      {state.user && <SystemMessages />}
    </AppBar>
  )
}

export default Navbar
